import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { REMEMBER_SECONDS } from '../gameslib/difficulty'
import { useGameSettings } from '../gameslib/gameSettings'
import { useTutorial } from '../gameslib/useTutorial'
import {
  drawShapeImage,
  getShapeAsset,
  preloadShapeAssets,
  randomShapeId,
} from '../gameslib/shapeAssets'
import { sfxSubmit, sfxTick } from '../gameslib/audio'
import { Countdown, MemoryTimer } from '../gamesshared/Countdown'
import { ArrowIcon, GameShell, IntroScreen, PlayerBanner } from '../gamesshared/IntroScreen'
import { PassDevice, PlayerSetup, WinnerScreen } from '../gamesshared/Multiplayer'
import { RoundResult, TotalScreen } from '../gamesshared/RoundResult'
import { TutorialCoach } from '../gamesshared/TutorialCoach'

function shapeCoach(step) {
  if (step === 'watch') return { message: 'Memorize the pose', gesture: 'watch', anchor: 'center' }
  if (step === 'move') return { message: 'Drag to move', gesture: 'move', anchor: 'center' }
  if (step === 'rotate') return { message: 'Twist to rotate', gesture: 'rotate', anchor: 'center' }
  if (step === 'submit') return { message: 'Tap to lock in', gesture: 'tap', anchor: 'submit' }
  if (step === 'done') return { message: 'Closer matches score higher', gesture: 'tap', anchor: 'submit' }
  return null
}

const BASE_RADIUS = 78

function randomPose(excludeIds = []) {
  return {
    shapeId: randomShapeId(excludeIds),
    rotation: Math.random() * Math.PI * 2,
    scale: 0.75 + Math.random() * 0.5,
    x: (Math.random() - 0.5) * 0.4,
    y: (Math.random() - 0.5) * 0.4,
  }
}

function drawGuides(ctx, w, h, dpr) {
  ctx.fillStyle = 'rgba(255,255,255,0.14)'
  const step = 40 * dpr
  for (let x = step; x < w; x += step) {
    for (let y = step; y < h; y += step) {
      ctx.beginPath()
      ctx.arc(x, y, 1.4 * dpr, 0, Math.PI * 2)
      ctx.fill()
    }
  }
  ctx.strokeStyle = 'rgba(255,255,255,0.28)'
  ctx.lineWidth = 1.5 * dpr
  ctx.beginPath()
  ctx.moveTo(w / 2, 16 * dpr)
  ctx.lineTo(w / 2, h - 16 * dpr)
  ctx.moveTo(16 * dpr, h / 2)
  ctx.lineTo(w - 16 * dpr, h / 2)
  ctx.stroke()

  ctx.strokeStyle = 'rgba(255,255,255,0.55)'
  ctx.lineWidth = dpr
  const c = 11 * dpr
  ctx.beginPath()
  ctx.moveTo(w / 2 - c, h / 2)
  ctx.lineTo(w / 2 + c, h / 2)
  ctx.moveTo(w / 2, h / 2 - c)
  ctx.lineTo(w / 2, h / 2 + c)
  ctx.stroke()
}

function poseCenter(pose, w, h) {
  return {
    cx: w / 2 + pose.x * w,
    cy: h / 2 + pose.y * h,
    radius: BASE_RADIUS * pose.scale,
  }
}

function drawScene(canvas, pose, opts = {}) {
  const {
    showGuides = true,
    showHandles = false,
    ghost = null,
    tint = '#f4efe6',
    ghostTint = 'rgba(244,239,230,0.28)',
  } = opts
  if (!canvas || !pose) return
  const parent = canvas.parentElement
  if (!parent) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const dpr = devicePixelRatio || 1
  const cssW = Math.max(1, parent.clientWidth)
  const cssH = Math.max(1, parent.clientHeight)
  if (canvas.width !== Math.floor(cssW * dpr) || canvas.height !== Math.floor(cssH * dpr)) {
    canvas.width = Math.floor(cssW * dpr)
    canvas.height = Math.floor(cssH * dpr)
  }
  const w = canvas.width
  const h = canvas.height

  ctx.clearRect(0, 0, w, h)
  ctx.fillStyle = '#001E3A'
  ctx.fillRect(0, 0, w, h)

  if (showGuides) drawGuides(ctx, w, h, dpr)

  if (ghost) {
    const g = poseCenter(ghost, w, h)
    drawShapeImage(
      ctx,
      ghost.shapeId,
      g.cx,
      g.cy,
      g.radius * dpr,
      ghost.rotation,
      ghostTint.startsWith('rgba') ? '#f4efe6' : ghostTint,
      ghostTint.startsWith('rgba') ? 0.28 : 0.3,
    )
  }

  const { cx, cy, radius } = poseCenter(pose, w, h)
  const r = radius * dpr

  if (showHandles) {
    ctx.strokeStyle = 'rgba(255,255,255,0.45)'
    ctx.lineWidth = 3.5 * dpr
    ctx.setLineDash([6 * dpr, 6 * dpr])
    ctx.beginPath()
    ctx.arc(cx, cy, r + 30 * dpr, 0, Math.PI * 2)
    ctx.stroke()
    ctx.setLineDash([])

    const hx = cx + Math.cos(pose.rotation - Math.PI / 2) * (r + 30 * dpr)
    const hy = cy + Math.sin(pose.rotation - Math.PI / 2) * (r + 30 * dpr)
    ctx.fillStyle = '#F29F00'
    ctx.beginPath()
    ctx.arc(hx, hy, 11 * dpr, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = 2 * dpr
    ctx.stroke()

    const sx = cx + Math.cos(pose.rotation + Math.PI / 4) * (r + 30 * dpr)
    const sy = cy + Math.sin(pose.rotation + Math.PI / 4) * (r + 30 * dpr)
    ctx.fillStyle = '#fff'
    ctx.fillRect(sx - 8 * dpr, sy - 8 * dpr, 16 * dpr, 16 * dpr)
    ctx.strokeStyle = 'rgba(0,0,0,0.35)'
    ctx.lineWidth = dpr
    ctx.strokeRect(sx - 8 * dpr, sy - 8 * dpr, 16 * dpr, 16 * dpr)
  }

  // Player answer tint: orange on result overlay when requested via tint
  drawShapeImage(ctx, pose.shapeId, cx, cy, r, pose.rotation, tint, 1)
}

function normalizeAngle(rad) {
  const twoPi = Math.PI * 2
  return ((rad % twoPi) + twoPi) % twoPi
}

/** Shortest arc between two angles (works even after many spins). */
function circularAngleDelta(a, b) {
  let d = Math.abs(a - b) % (Math.PI * 2)
  if (d > Math.PI) d = Math.PI * 2 - d
  return d
}

/** Asymmetric SVGs — only the true orientation counts (no n-fold symmetry) */
function scorePoseDetailed(target, guess) {
  const dRot = circularAngleDelta(target.rotation, guess.rotation)
  // Edge alignment: ~0° = 100, ~28° already near 0 (was 75° — far too loose)
  const rotScore = Math.min(
    100,
    100 * Math.pow(Math.max(0, 1 - dRot / ((28 * Math.PI) / 180)), 1.45),
  )

  const dPos = Math.hypot(target.x - guess.x, target.y - guess.y)
  const posScore = Math.min(100, 100 * Math.pow(Math.max(0, 1 - dPos / 0.28), 1.25))

  const dScale = Math.abs(target.scale - guess.scale)
  const scaleScore = Math.min(100, 100 * Math.pow(Math.max(0, 1 - dScale / 0.32), 1.2))

  // Rotation dominates — wrong edge = wrong answer on unique art
  const total = posScore * 0.28 + rotScore * 0.52 + scaleScore * 0.2
  return {
    total: Math.max(0, Math.min(100, total)),
    pos: posScore,
    rot: rotScore,
    scale: scaleScore,
    dRotDeg: (dRot * 180) / Math.PI,
    dPos,
    dScale,
  }
}

function deg(rad) {
  return Math.round((((rad * 180) / Math.PI) % 360 + 360) % 360)
}

function hitTest(e, canvas, pose) {
  const rect = canvas.getBoundingClientRect()
  const x = e.clientX - rect.left
  const y = e.clientY - rect.top
  const w = rect.width
  const h = rect.height
  const { cx, cy, radius } = poseCenter(pose, w, h)
  const dist = Math.hypot(x - cx, y - cy)

  const ringR = radius + 30
  const hx = cx + Math.cos(pose.rotation - Math.PI / 2) * ringR
  const hy = cy + Math.sin(pose.rotation - Math.PI / 2) * ringR
  const sx = cx + Math.cos(pose.rotation + Math.PI / 4) * ringR
  const sy = cy + Math.sin(pose.rotation + Math.PI / 4) * ringR

  if (Math.hypot(x - hx, y - hy) < 28) return 'rotate'
  if (Math.hypot(x - sx, y - sy) < 26) return 'scale'
  if (dist <= radius * 1.05) return 'move'
  if (dist <= ringR + 22) return 'rotate'
  return null
}

export function ShapeGame() {
  const { rounds, setRounds, setModeLabel } = useGameSettings()
  const tutorial = useTutorial('shape')
  const [phase, setPhase] = useState('intro')
  const [difficulty, setDifficulty] = useState('easy')
  const [round, setRound] = useState(0)
  const [targets, setTargets] = useState([])
  const [guess, setGuess] = useState(() => randomPose())
  const [scores, setScores] = useState([])
  const [lastScore, setLastScore] = useState(0)
  const [breakdown, setBreakdown] = useState(null)
  const [playMode, setPlayMode] = useState('solo')
  const [players, setPlayers] = useState([])
  const [playerIndex, setPlayerIndex] = useState(0)
  const [dragMode, setDragMode] = useState(null)
  const [assetsReady, setAssetsReady] = useState(false)

  const canvasRef = useRef(null)
  const submitRef = useRef(null)
  const nextRef = useRef(null)
  const resultYouRef = useRef(null)
  const drag = useRef(null)
  const lastPtr = useRef({ x: 0, y: 0 })
  const lastAngle = useRef(null)
  const pointers = useRef(new Map())
  const pinch = useRef(null)
  const guessRef = useRef(guess)
  const tutorialRef = useRef(tutorial)
  const isTouchUi = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches

  const target = targets[round]
  const currentPlayer = players[playerIndex]
  const playRounds = tutorial.active ? 1 : rounds
  const rememberS = tutorial.active ? Math.max(REMEMBER_SECONDS.easy, 3.5) : REMEMBER_SECONDS[difficulty]
  guessRef.current = guess
  tutorialRef.current = tutorial

  useEffect(() => {
    let alive = true
    void preloadShapeAssets().then(() => {
      if (alive) setAssetsReady(true)
    })
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    setModeLabel(playMode === 'multi' ? 'multi' : 'solo')
  }, [playMode, setModeLabel])

  const makeTargets = (count = rounds) => {
    const used = []
    return Array.from({ length: count }, () => {
      const pose = randomPose(used)
      used.push(pose.shapeId)
      return pose
    })
  }

  const resetTurn = (shared = targets) => {
    setScores([])
    setRound(0)
    setLastScore(0)
    setBreakdown(null)
    setGuess({ shapeId: shared?.[0]?.shapeId || randomShapeId(), rotation: 0, scale: 1, x: 0, y: 0 })
    if (shared?.length) setTargets(shared)
  }

  const beginPlay = (asTutorial) => {
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
    if (asTutorial) tutorial.start('watch')
    else if (tutorial.active) tutorial.finish()
    const next = makeTargets(asTutorial ? 1 : rounds)
    setTargets(next)
    resetTurn(next)
    setPhase('countdown')
  }

  const startSolo = () => beginPlay(tutorial.needsTutorial)
  const startTeach = () => beginPlay(true)

  const exitTutorial = () => {
    tutorial.skip()
    setPhase('intro')
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
  }

  const openMulti = () => {
    if (tutorial.active) tutorial.finish()
    setPlayMode('multi')
    setPhase('setup')
  }

  const confirmPlayers = (list) => {
    if (tutorial.active) tutorial.finish()
    const next = makeTargets()
    setTargets(next)
    setPlayers(list.map((p) => ({ ...p, scores: [] })))
    setPlayerIndex(0)
    setPlayMode('multi')
    resetTurn(next)
    setPhase('pass')
  }

  const finishPlayerTurn = (finalScores) => {
    if (playMode === 'solo') {
      setPhase('total')
      return
    }
    const updated = players.map((p, i) => (i === playerIndex ? { ...p, scores: finalScores } : p))
    setPlayers(updated)
    if (playerIndex >= players.length - 1) setPhase('winner')
    else {
      setPlayerIndex((i) => i + 1)
      setPhase('pass')
    }
  }

  const onCountdownDone = useCallback(() => {
    if (tutorial.active) tutorial.go('watch')
    setPhase('stimulus')
  }, [tutorial])

  const onStimulusDone = useCallback(() => {
    if (tutorial.active) tutorial.go('move')
    setGuess({
      shapeId: target.shapeId,
      rotation: (target.rotation + Math.PI * (0.35 + Math.random() * 0.5)) % (Math.PI * 2),
      scale: Math.min(1.4, Math.max(0.55, target.scale + (Math.random() - 0.5) * 0.35)),
      x: Math.max(-0.35, Math.min(0.35, target.x + (Math.random() - 0.5) * 0.35)),
      y: Math.max(-0.35, Math.min(0.35, target.y + (Math.random() - 0.5) * 0.35)),
    })
    setPhase('input')
  }, [target, tutorial])

  useEffect(() => {
    if (!assetsReady) return
    if (phase !== 'stimulus' && phase !== 'input') return
    const pose = phase === 'stimulus' ? target : guess
    drawScene(canvasRef.current, pose, {
      showGuides: true,
      showHandles: phase === 'input' && !isTouchUi,
      tint: '#f4efe6',
    })
  }, [phase, target, guess, isTouchUi, assetsReady])

  useLayoutEffect(() => {
    if (!assetsReady || phase !== 'result' || !target) return
    const draw = () => {
      drawScene(resultYouRef.current, guess, {
        showGuides: true,
        ghost: target,
        tint: '#F29F00',
      })
    }
    draw()
    const t = window.setTimeout(draw, 50)
    return () => clearTimeout(t)
  }, [phase, target, guess, assetsReady])

  const pointerAngle = (e, pose) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const { cx, cy } = poseCenter(pose, rect.width, rect.height)
    return Math.atan2(e.clientY - (rect.top + cy), e.clientX - (rect.left + cx))
  }

  const twoFingerMetrics = () => {
    const pts = [...pointers.current.values()]
    if (pts.length < 2) return null
    const [a, b] = pts
    return {
      dist: Math.hypot(b.x - a.x, b.y - a.y),
      angle: Math.atan2(b.y - a.y, b.x - a.x),
      midX: (a.x + b.x) / 2,
      midY: (a.y + b.y) / 2,
    }
  }

  const beginPinch = () => {
    const m = twoFingerMetrics()
    if (!m) return
    const pose = guessRef.current
    pinch.current = {
      startDist: m.dist,
      startAngle: m.angle,
      startScale: pose.scale,
      startRotation: pose.rotation,
      startMidX: m.midX,
      startMidY: m.midY,
      startX: pose.x,
      startY: pose.y,
    }
    drag.current = 'pinch'
    setDragMode('pinch')
  }

  const advanceTutorialFromDrag = (mode) => {
    const tut = tutorialRef.current
    if (!tut.active) return
    if (mode === 'move' && tut.step === 'move') {
      tut.go('rotate')
      return
    }
    if (
      (mode === 'rotate' || mode === 'scale' || mode === 'pinch') &&
      (tut.step === 'move' || tut.step === 'rotate')
    ) {
      tut.go('submit')
    }
  }

  const onPointerDown = (e) => {
    if (phase !== 'input') return
    e.preventDefault()
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }

    if (pointers.current.size >= 2) {
      beginPinch()
      advanceTutorialFromDrag('pinch')
      return
    }

    const mode = isTouchUi ? 'move' : hitTest(e, canvasRef.current, guessRef.current)
    if (!mode) return
    drag.current = mode
    setDragMode(mode)
    lastPtr.current = { x: e.clientX, y: e.clientY }
    if (mode === 'rotate') lastAngle.current = pointerAngle(e, guessRef.current)
  }

  const onPointerMove = (e) => {
    if (phase !== 'input') return
    if (!pointers.current.has(e.pointerId) && !drag.current) return
    if (pointers.current.has(e.pointerId)) {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    }

    const canvas = canvasRef.current
    if (!canvas) return

    if (pointers.current.size >= 2 && pinch.current) {
      const m = twoFingerMetrics()
      if (!m || pinch.current.startDist < 8) return
      const p = pinch.current
      const scaleDelta = m.dist / p.startDist
      const nextScale = Math.max(0.5, Math.min(1.5, p.startScale * scaleDelta))
      let rotDelta = m.angle - p.startAngle
      if (rotDelta > Math.PI) rotDelta -= Math.PI * 2
      if (rotDelta < -Math.PI) rotDelta += Math.PI * 2
      const dx = (m.midX - p.startMidX) / canvas.clientWidth
      const dy = (m.midY - p.startMidY) / canvas.clientHeight
      void sfxTick()
      setGuess((g) => ({
        ...g,
        scale: nextScale,
        rotation: normalizeAngle(p.startRotation + rotDelta),
        x: Math.max(-0.42, Math.min(0.42, p.startX + dx)),
        y: Math.max(-0.42, Math.min(0.42, p.startY + dy)),
      }))
      setDragMode('pinch')
      advanceTutorialFromDrag('pinch')
      return
    }

    if (!drag.current || drag.current === 'pinch') return
    const pose = guessRef.current

    if (drag.current === 'move') {
      const dx = e.clientX - lastPtr.current.x
      const dy = e.clientY - lastPtr.current.y
      lastPtr.current = { x: e.clientX, y: e.clientY }
      void sfxTick()
      setGuess((g) => ({
        ...g,
        x: Math.max(-0.42, Math.min(0.42, g.x + dx / canvas.clientWidth)),
        y: Math.max(-0.42, Math.min(0.42, g.y + dy / canvas.clientHeight)),
      }))
      advanceTutorialFromDrag('move')
      return
    }

    if (drag.current === 'rotate') {
      const ang = pointerAngle(e, pose)
      if (lastAngle.current != null) {
        let delta = ang - lastAngle.current
        if (delta > Math.PI) delta -= Math.PI * 2
        if (delta < -Math.PI) delta += Math.PI * 2
        void sfxTick()
        setGuess((g) => ({ ...g, rotation: normalizeAngle(g.rotation + delta) }))
      }
      lastAngle.current = ang
      advanceTutorialFromDrag('rotate')
      return
    }

    if (drag.current === 'scale') {
      const rect = canvas.getBoundingClientRect()
      const { cx, cy } = poseCenter(pose, rect.width, rect.height)
      const dist = Math.hypot(e.clientX - (rect.left + cx), e.clientY - (rect.top + cy))
      const nextScale = Math.max(0.5, Math.min(1.5, (dist - 30) / BASE_RADIUS))
      void sfxTick()
      setGuess((g) => ({ ...g, scale: nextScale }))
      advanceTutorialFromDrag('scale')
    }
  }

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId)
    if (pointers.current.size >= 2) {
      beginPinch()
      return
    }
    if (pointers.current.size === 1) {
      const [pt] = pointers.current.values()
      pinch.current = null
      drag.current = 'move'
      setDragMode('move')
      lastPtr.current = { x: pt.x, y: pt.y }
      lastAngle.current = null
      return
    }
    drag.current = null
    pinch.current = null
    lastAngle.current = null
    setDragMode(null)
  }

  const submit = () => {
    const tut = tutorialRef.current
    if (tut.active && tut.step !== 'submit' && tut.step !== 'done') return
    void sfxSubmit()
    const liveGuess = {
      ...guessRef.current,
      rotation: normalizeAngle(guessRef.current.rotation),
    }
    const detail = scorePoseDetailed(target, liveGuess)
    setLastScore(detail.total)
    setBreakdown(detail)
    setScores((prev) => [...prev, detail.total])
    if (tut.active) tut.go('done')
    setPhase('result')
  }

  const next = () => {
    if (tutorial.active) {
      tutorial.finish()
      setPhase('intro')
      return
    }
    if (round + 1 >= playRounds) {
      const finalScores = scores.length >= playRounds ? scores : [...scores, lastScore]
      finishPlayerTurn(finalScores)
    } else {
      setRound((r) => r + 1)
      setBreakdown(null)
      setPhase('countdown')
    }
  }

  const shapeLabel = target ? getShapeAsset(target.shapeId)?.label : 'shape'
  const coach = tutorial.active ? shapeCoach(tutorial.step) : null
  const submitBlocked = tutorial.active && tutorial.step !== 'submit' && tutorial.step !== 'done'
  const coachTarget =
    tutorial.step === 'done'
      ? nextRef
      : tutorial.step === 'move' || tutorial.step === 'rotate'
        ? canvasRef
        : tutorial.step === 'submit'
          ? submitRef
          : null

  return (
    <GameShell>
      {phase === 'intro' && (
        <IntroScreen
          title="shape"
          blurb={`You see icons constantly. Let's see how well you remember them.\nWe'll show you ${rounds} illustrations, then you recreate their pose from memory.`}
          onSolo={startSolo}
          onMulti={openMulti}
          onTeach={startTeach}
          difficulty={difficulty}
          onDifficulty={setDifficulty}
          rounds={rounds}
          onRounds={setRounds}
        />
      )}

      {phase === 'setup' && (
        <PlayerSetup onConfirm={confirmPlayers} onBack={() => setPhase('intro')} rounds={rounds} onRounds={setRounds} />
      )}

      {phase === 'pass' && currentPlayer && (
        <PassDevice
          player={currentPlayer}
          index={playerIndex}
          total={players.length}
          rounds={rounds}
          onReady={() => {
            resetTurn(targets)
            setPhase('countdown')
          }}
        />
      )}

      {phase === 'countdown' && (
        <div className="stage">
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          {!tutorial.active && (
            <div className="round-pill">
              {round + 1} / {playRounds}
            </div>
          )}
          <Countdown onDone={onCountdownDone} />
        </div>
      )}

      {(phase === 'stimulus' || phase === 'input') && (
        <div className="stage">
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          {!tutorial.active && (
            <div className="round-pill">
              {round + 1} / {playRounds}
            </div>
          )}
          <canvas
            ref={canvasRef}
            className="shape-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
          {phase === 'stimulus' && (
            <MemoryTimer
              seconds={rememberS * 0.85}
              label={`Memorize ${shapeLabel}`}
              onDone={onStimulusDone}
            />
          )}
          {phase === 'input' && (
            <>
              <p className="shape-hint">
                {dragMode === 'pinch'
                  ? 'Pinch to scale · twist to rotate'
                  : dragMode === 'rotate'
                    ? 'Rotating'
                    : dragMode === 'scale'
                      ? 'Scaling'
                      : dragMode === 'move'
                        ? 'Moving'
                        : isTouchUi
                          ? 'One finger: move · two fingers: pinch size / twist rotate'
                          : 'Drag to move · edge / orange knob to rotate · white square to scale'}
              </p>
              <div className="shape-live-meta">
                {deg(guess.rotation)}° · ×{guess.scale.toFixed(2)}
              </div>
              <button
                type="button"
                ref={submitRef}
                className={`go-btn${tutorial.step === 'submit' ? ' is-tutor-pulse' : ''}`}
                aria-label="Submit answer"
                onClick={submit}
                disabled={submitBlocked}
              >
                <ArrowIcon />
              </button>
            </>
          )}
          <div className="watermark">Memory</div>
          {coach && (phase === 'stimulus' || phase === 'input') && (
            <TutorialCoach {...coach} onSkip={exitTutorial} targetRef={coachTarget} />
          )}
        </div>
      )}

      {phase === 'result' && target && (
        <div className="shape-result">
          <RoundResult
            score={lastScore}
            round={tutorial.active ? 1 : round + 1}
            totalRounds={playRounds}
            onNext={next}
            nextButtonRef={nextRef}
            scoreColor="#fff"
            layout="stack"
          >
            <div className="shape-compare__single">
              <canvas ref={resultYouRef} className="shape-compare__canvas" />
              <div className="shape-compare__meta">
                <span>Yours (orange) · ghost = original</span>
                <strong>
                  {deg(guess.rotation)}° · ×{guess.scale.toFixed(2)}
                  <span className="shape-compare__vs">
                    {' '}
                    vs {deg(target.rotation)}° · ×{target.scale.toFixed(2)}
                  </span>
                </strong>
              </div>
            </div>
          </RoundResult>
          {breakdown && (
            <div className="shape-breakdown">
              <span>Pos {breakdown.pos.toFixed(0)}%</span>
              <span>Rot {breakdown.rot.toFixed(0)}%</span>
              <span>Scale {breakdown.scale.toFixed(0)}%</span>
            </div>
          )}
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} targetRef={coachTarget} />}
        </div>
      )}

      {phase === 'total' && <TotalScreen scores={scores} onReplay={startSolo} />}

      {phase === 'winner' && (
        <WinnerScreen
          players={players}
          onReplay={() => {
            const nextTargets = makeTargets()
            setTargets(nextTargets)
            setPlayers((prev) => prev.map((p) => ({ ...p, scores: [] })))
            setPlayerIndex(0)
            resetTurn(nextTargets)
            setPhase('pass')
          }}
          onHome={() => {
            if (tutorial.active) tutorial.finish()
            setPhase('intro')
            setPlayMode('solo')
            setPlayers([])
            setPlayerIndex(0)
          }}
        />
      )}
    </GameShell>
  )
}
